import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { QueryProvider } from './providers/QueryProvider';
import { ProtectedRoute } from './components/auth';
import { ErrorBoundary, MainLayout } from './components/layout';
import { LoginPage } from './pages/auth';
import { DashboardPage, ResumePage, ProfilePage, ApplicationsPage, MessagesPage, VideosPage, SavedJobsPage } from './pages/dashboard';
import { TestimonialPage } from './pages/dashboard/TestimonialPage';
import { HomePage } from './pages/home';
import { JobsPage, JobDetailsPage } from './pages/jobs';
import { AboutPage, ContactPage, PrivacyPolicyPage, TermsOfServicePage, ServicesPage } from './pages/static';
import { AdminDashboardPage, AdminUsersPage, AdminJobsPage, AdminJobCreatePage, AdminJobApplicationsPage, AdminApplicationsPage, AdminResumesPage, AdminCandidateProfilePage, AdminCandidatesPage, AdminMessagesPage, AdminAnalyticsPage, AdminThemeSettingsPage, AdminSystemSettingsPage, AdminFileManagerPage, AdminTestimonialsPage, AdminCompaniesPage, AdminCompanyDetailPage } from './pages/admin';
import { AdminJobDetailView } from './pages/admin/AdminJobDetailView';
import { usePageMetadata } from './hooks/usePageMetadata';
import { AnalyticsProvider } from './components/analytics/AnalyticsProvider';
import { CookieConsent } from './components/ui/CookieConsent';
import { usePageTracking } from './hooks/useAnalytics';



function AppContent() {
  // Update page metadata (title and favicon) based on system settings
  usePageMetadata();
  
  // Track page views automatically
  usePageTracking();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<MainLayout showBreadcrumbs={false}><HomePage /></MainLayout>} />
              <Route path="/jobs" element={<MainLayout><JobsPage /></MainLayout>} />
              <Route path="/jobs/:id" element={<MainLayout><JobDetailsPage /></MainLayout>} />
              <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
              <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
              <Route path="/services" element={<MainLayout><ServicesPage /></MainLayout>} />
              <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
              <Route path="/terms" element={<MainLayout><TermsOfServicePage /></MainLayout>} />
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes - no footer for dashboard pages */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <DashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/resumes"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ResumePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/profile"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ProfilePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/applications"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <ApplicationsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/messages"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <MessagesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/videos"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <VideosPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/testimonials"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <TestimonialPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/saved-jobs"
                element={
                  <ProtectedRoute>
                    <MainLayout showFooter={false}>
                      <SavedJobsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              
              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminJobsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/create"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminJobCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:id/edit"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminJobCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:jobId/applications"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminJobApplicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/jobs/:jobId"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminJobDetailView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/applications"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminApplicationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/resumes"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminResumesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCompaniesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies/:id"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCompanyDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/candidates"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCandidatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/candidates/:id"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminCandidateProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/messages"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminMessagesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/theme"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminThemeSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/testimonials"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminTestimonialsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminSystemSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/files"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminFileManagerPage />
                  </ProtectedRoute>
                }
              />
              
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <QueryProvider>
          <AnalyticsProvider>
            <ToastProvider>
              <ThemeProvider>
                <AuthProvider>
                  <AppContent />
                  <CookieConsent />
                </AuthProvider>
              </ThemeProvider>
            </ToastProvider>
          </AnalyticsProvider>
        </QueryProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
